import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useJsApiLoader } from '@react-google-maps/api';

const LIBRARIES = ['places'];

const GoogleMapsContext = createContext({ isLoaded: false, loadError: null });

function GoogleMapsLoader({ children, onLoaded, onError }) {
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
    libraries: LIBRARIES,
  });

  useEffect(() => {
    if (isLoaded) onLoaded();
    if (loadError) onError(loadError);
  }, [isLoaded, loadError, onLoaded, onError]);

  return children;
}

export function GoogleMapsProvider({ children }) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [retryKey, setRetryKey] = useState(0);

  const handleLoaded = useCallback(() => {
    setIsLoaded(true);
    setLoadError(null);
  }, []);

  const handleError = useCallback((error) => {
    setLoadError(error);
    setIsLoaded(false);
  }, []);

  const retry = useCallback(() => {
    setLoadError(null);
    setIsLoaded(false);
    // Remove any existing Google Maps script tags so the loader can try again
    const scripts = document.querySelectorAll('script[src*="maps.googleapis.com"]');
    scripts.forEach(s => s.remove());
    // Clear the google maps object to allow re-initialization
    if (window.google && window.google.maps) {
      delete window.google.maps;
    }
    setRetryKey(k => k + 1);
  }, []);

  return (
    <GoogleMapsContext.Provider value={{ isLoaded, loadError, retry }}>
      <GoogleMapsLoader key={retryKey} onLoaded={handleLoaded} onError={handleError}>
        {children}
      </GoogleMapsLoader>
    </GoogleMapsContext.Provider>
  );
}

export function useGoogleMaps() {
  return useContext(GoogleMapsContext);
}
